import moment from "moment";
import { isEmpty, filter } from "lodash";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ChatbotContent } from "components";
import {
  fetchMascotSettings,
  fetchPropertyData,
  getMascotManagement,
  INITIAL_REPLY_OPTIONS,
  renderMascot,
  hayleyAnimation,
  getConversationId,
  createItemRecord,
  DEFAULT_SESSION_ID,
  getPromotionInfo,
  fetchGtmId,
  addUTMScript,
  getWidgetHeight,
  getWidgetRefHeight,
  INITIAL_REPLIES,
  getUrlParam,
  getBedroomTypes,
  getPriceBrackets,
  getUnits,
} from "utils";
import "./styles/animations.css";
import {
  getAppSSID,
  getArtieLogo,
  getHeaderBgColor,
  getIsMaximized,
  getIsOpen,
  getIsParentView,
  getLogoType,
  getVisibleMascot,
  getMultiLanguage,
  selectAPIKey,
  selectIsPhotoTourScreen,
  selectIsVideoTourScreen,
  selectMascotSettings,
  selectMessages,
  selectPromotionOffer,
  selectTransStatus,
  setAPIKey,
  setAppSSID,
  setArtieLogo,
  setButtonBgColor,
  setButtonBorderColor,
  setButtonTxtColor,
  setChatbotTitleDetails,
  setConversationID,
  setCustomButtons,
  setCustomFormQuestion,
  setEnabledScheduleATour,
  setFilterOptions,
  setFontFamily,
  setIsJAI,
  setIsMaximized,
  setIsMultiLanguage,
  setIsOpen,
  setIsParentView,
  setIsPriceFilter,
  setIsSpanishLanguage,
  setIsPhoneNumberRequired,
  setIsmobileWindow,
  setLinkColor,
  setLogoType,
  setMascotSettings,
  setMessageColor,
  setPromotionOffer,
  setPropertyId,
  setPropertyName,
  setShowContactSMSOpt,
  setShowFaq,
  setShowFeedback,
  setShowTourSMSOpt,
  setSiteId,
  setOrgId,
  setTourLinkInHyTours,
  setTourShowInPopup,
  setVisibleCommunityInfo,
  setVisibleFindAHome,
  setVisibleInPersonTour,
  setVisibleLiveVideoTour,
  setVisiblePhotos,
  setVisibleScheduleATour,
  setVisibleSelfGuidedTour,
  setVisibleVideos,
  setVisibleVisitResidentPortal,
  unitSliceData,
  setQualificationButton,
  setQualificationButtonEnabled,
  setContactPromotionMsg,
  setContactTransactionalMsg,
  setShowContactPromotionMsg,
  setShowContactTransactionalMsg,
  setShowTourTransactionalMsg,
  setTourTransactionalMsg,
  setShowTourPromotionMsg,
  setTourPromotionMsg,
  loadInitialMessages,
  removeAndAddMessages,
  setSelfTourLink,
  selectIsFetchManagement,
  setReplies,
  setIsDisabled,
  setStandalonePage,
  setStandaloneParams,
  setIsFeedbackModalVisible,
  setFormDisclaimer,
  getOrgId,
  selectPropertyId,
} from "slices";
import { getSelfTourLink, createEventSignal, getHayleyChatDisclaimer, getPrivacyPolicyDisclaimer, getFormattedDisclaimer } from "utils/methods";
import { getPriceRange, isValidDateRange } from "components/UnitsView/utils";
import { setAllFloorPlans, setBedRooms, setBudgets, setIsUnitTourScreen, setUnitFilters, setUnits } from "slices/units-slice/index";
import { setPrivacyPolicyDisclaimer } from "slices/mascot-settings-slice/index";
import { ERROR_MSG } from "utils/constants/index";

const Icon = React.lazy(() => import("components/Icon"));
const HotKeys = React.lazy(() => import("components/HotKeys"));
const VideoTourBox = React.lazy(() => import("components/VideoTourBox"));
const PhotosView = React.lazy(() => import("components/PhotosView"));
const UnitsView = React.lazy(() => import("components/UnitsView"));

const App = () => {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [position, setPosition] = useState("bottom-right");
  const [parentWindowWidth, setParentWindowWidth] = useState(null);
  const [parentWindowHeight, setParentWindowHeight] = useState(null);
  const [widgetHeight, setWidgetHeight] = useState("120px");
  const [hotkeysWidth, setHotkeysWidth] = useState(0);

  const [isContentLoading, setIsContentLoading] = useState(false);

  const hotkeysRef = useRef(null);
  const ssidRef = useRef(null);

  const isOpen = useSelector(getIsOpen);
  const isMaximized = useSelector(getIsMaximized);
  const isVideoTourScreen = useSelector(selectIsVideoTourScreen);
  const promotionOffer = useSelector(selectPromotionOffer);
  const isPhotoTourScreen = useSelector(selectIsPhotoTourScreen);
  const transStatus = useSelector(selectTransStatus);
  const isParentView = useSelector(getIsParentView);
  const { isUnitTourScreen } = useSelector(unitSliceData);
  const artieLogo = useSelector(getArtieLogo);
  const logoType = useSelector(getLogoType);
  const mascotSettings = useSelector(selectMascotSettings);
  const backgroundColor = useSelector(getHeaderBgColor);
  const SSID = useSelector(getAppSSID);
  const messages = useSelector(selectMessages);
  const visibleMascot = useSelector(getVisibleMascot);
  const apiKey = useSelector(selectAPIKey);
  const isMultiLanguage = useSelector(getMultiLanguage);
  const isFetchManagement = useSelector(selectIsFetchManagement);
  const orgId = useSelector(getOrgId);
  const propertyId = useSelector(selectPropertyId);

  const mascotHeight = isEmpty(artieLogo) ? 88 : 98;
  const isMobile = parentWindowWidth ? parentWindowWidth < 768 : false;
  const shouldShowContent = useMemo(() => !isContentLoading && transStatus === "success", [isContentLoading, transStatus]);

  const isOpenRef = useRef(isOpen);

  const getMascot = useCallback(
    (property_id, api_key) => {
      setIsContentLoading(true);

      getMascotManagement(property_id, api_key)
        .then((res) => {
          const {
            image_data,
            icon_url,
            icon_file_type,
            site_id,
            schedule_a_tour,
            find_a_home,
            in_person_tour,
            live_video_tour,
            self_guided_tour,
            visit_resident_portal,
            self_tour_link_in_hytours,
            self_tour_show_in_popup,
            photos,
            videos,
            community_info,
            widget_title,
            chatbot_title,
            show_faq,
            support_multi_language,
            support_spanish_language,
            make_phone_number_required,
            hide_price_range_filter,
            show_feedback_filter,
            custom_form_questions,
            sms_optins_hyforms,
            sms_optins_hytours,
            tour_schedule_enabled,
            position: chatbotPosition,
            custom_buttons,
            qualification_button,
            qualification_button_enabled,
            sms_optins_hyforms_transactional_message_enabled,
            sms_optins_hyforms_transactional_message,
            sms_optins_hyforms_promotional_message_enabled,
            sms_optins_hyforms_promotional_message,
            sms_optins_hytours_transactional_message_enabled,
            sms_optins_hytours_promotional_message_enabled,
            sms_optins_hytours_transactional_message,
            sms_optins_hytours_promotional_message,
            org_id,
          } = res.data;

          if (!getIsJAI()) {
            setPosition(chatbotPosition || "bottom-right");
          }

          const img = icon_url || image_data;

          if (!artieLogo) {
            dispatch(setArtieLogo(img));
          }

          dispatch(setLogoType(icon_file_type));
          dispatch(setSiteId(site_id));
          dispatch(setOrgId(org_id));
          dispatch(setVisibleScheduleATour(schedule_a_tour));
          dispatch(setVisibleFindAHome(find_a_home));
          dispatch(setVisibleInPersonTour(in_person_tour));
          dispatch(setVisibleLiveVideoTour(live_video_tour));
          dispatch(setVisibleSelfGuidedTour(self_guided_tour));
          dispatch(setTourLinkInHyTours(self_tour_link_in_hytours));
          dispatch(setShowFaq(show_faq));
          dispatch(setTourShowInPopup(self_tour_show_in_popup));
          dispatch(setVisibleVisitResidentPortal(visit_resident_portal));
          dispatch(setVisiblePhotos(photos));
          dispatch(setVisibleVideos(videos));
          dispatch(setVisibleCommunityInfo(community_info));
          dispatch(setIsSpanishLanguage(support_spanish_language));
          dispatch(setIsPhoneNumberRequired(make_phone_number_required));
          dispatch(setIsMultiLanguage(support_multi_language));
          dispatch(setIsPriceFilter(hide_price_range_filter));
          dispatch(setShowFeedback(show_feedback_filter));
          dispatch(setCustomFormQuestion(custom_form_questions));
          dispatch(setShowTourSMSOpt(sms_optins_hytours));
          dispatch(setShowContactSMSOpt(sms_optins_hyforms));
          dispatch(setCustomButtons(custom_buttons));
          dispatch(setQualificationButton(qualification_button));
          dispatch(setQualificationButtonEnabled(qualification_button_enabled));
          dispatch(setShowContactTransactionalMsg(sms_optins_hyforms_transactional_message_enabled));
          dispatch(setContactTransactionalMsg(sms_optins_hyforms_transactional_message));
          dispatch(setShowContactPromotionMsg(sms_optins_hyforms_promotional_message_enabled));
          dispatch(setContactPromotionMsg(sms_optins_hyforms_promotional_message));
          dispatch(setShowTourTransactionalMsg(sms_optins_hytours_transactional_message_enabled));
          dispatch(setTourTransactionalMsg(sms_optins_hytours_transactional_message));
          dispatch(setShowTourPromotionMsg(sms_optins_hytours_promotional_message_enabled));
          dispatch(setTourPromotionMsg(sms_optins_hytours_promotional_message));

          if (tour_schedule_enabled === false) {
            dispatch(setEnabledScheduleATour(false));
          }

          dispatch(
            setChatbotTitleDetails({
              widget_title: widget_title,
              chatbot_title: chatbot_title,
            })
          );

          // Set initial reply option
          const filteredOptions = filter(INITIAL_REPLY_OPTIONS, (o) => {
            if (o.key === "find_home" && !find_a_home) {
              return false;
            }

            if (o.key === "visit_resident_portal" && !visit_resident_portal) {
              return false;
            }

            if (o.key === "photos" && !photos) {
              return false;
            }

            if (o.key === "videos" && !videos) {
              return false;
            }

            if (o.key === "community_info" && !community_info) {
              return false;
            }

            if (o.key === "schedule_tour" && !(in_person_tour || live_video_tour || self_guided_tour)) {
              return false;
            }

            if (o.key === "faqs" && !show_faq) {
              return false;
            }

            if (
              o.key === "do_i_qualify" &&
              (!qualification_button_enabled || qualification_button.qualification?.length === 0 || qualification_button.question?.length === 0)
            ) {
              return false;
            }

            return true;
          });

          const filteredKeys = filteredOptions?.map((o) => o.key);
          const filterOptions = INITIAL_REPLY_OPTIONS?.filter((o) => !filteredKeys.includes(o.key));
          const filterOptionKeys = filterOptions?.map((o) => o.key);

          if (filterOptionKeys?.length) {
            dispatch(setFilterOptions(filterOptionKeys));
          }
        })
        .catch((err) => {
          /** @todo Add error handling part */
        })
        .finally(() => setIsContentLoading(false));

      getSelfTourLink(`?api_key=${api_key}&property_id=${property_id}`)
        .then((res) => {
          const self_tour_link = res.data;

          dispatch(setSelfTourLink(self_tour_link));
        })
        .catch((err) => {});
    },
    [artieLogo, dispatch]
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const getGtmId = async (property_id) => {
    // If chatbot is NOT opened in standalone mode then don't add GTMs
    if (window.self != window.top) {
      return;
    }

    const encoded_property_id = btoa(`Tenant-${property_id}`);

    fetchGtmId(encoded_property_id)
      .then((response) => response.json())
      .then((result) => addUTMScript(result.data.websiteGtmScript.noscript, result.data.websiteGtmScript.script, result.data.websiteGtmScript.hytoursScript))
      .catch((error) => {});
  };

  const getPropertyData = useCallback(() => {
    const property_id = getPropertyId();
    const isJAI = getIsJAI();

    if (!property_id) {
      return;
    }

    dispatch(setIsJAI(isJAI));
    dispatch(setPropertyId(property_id));

    fetchPropertyData(property_id)
      .then((res) => {
        const { api_key, button_background_color, hayley_access, button_font_color, button_border_color, font_family, text_color, link_color, property_name } =
          res.data;
        dispatch(setAPIKey(api_key));
        dispatch(setButtonBgColor(button_background_color));
        dispatch(setButtonTxtColor(button_font_color));
        dispatch(setButtonBorderColor(button_border_color));
        dispatch(setFontFamily(font_family));
        dispatch(setMessageColor(text_color));
        dispatch(setLinkColor(link_color));
        dispatch(setPropertyName(property_name));

        // "hayley_access" is a setting managed by the sub-orgs app.
        // If "hayley_access" is true or the URL contains `page=available-units` or `page=contact-me`,
        // the chatbot will be displayed to ensure it appears on standalone pages when needed.
        // If "hayley_access" is false, the chatbot will not be displayed.
        const page = getUrlParam("page");

        if (hayley_access || page === "available-units" || page === "contact-me") {
          setIsShow(true);
        } else {
          setIsShow(false);
        }
      })
      .catch((err) => {
        /** @todo Add error handling part */
      });
  }, [dispatch]);

  // Getting property_id from the URL
  const getPropertyId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const property_id = urlParams.get("property_id");

    return property_id;
  };

  // Getting property_id from the URL
  const getIsJAI = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isJAI = urlParams.get("isjai");

    return isJAI;
  };

  const getShortUrl = useCallback(() => {
    if (getIsJAI()) {
      dispatch(setAppSSID(DEFAULT_SESSION_ID));
    }

    if (window.top != window.self) {
      dispatch(setIsParentView(false));
    } else {
      dispatch(setIsMaximized(true));
      dispatch(setIsOpen(true));
      dispatch(setIsParentView(true));
    }
  }, [dispatch]);

  // Getting position from the URL
  const getPosition = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const p = urlParams.get("position");

    if (p) {
      setPosition(p);
    }
  };

  // Getting parent window width
  const getParentWindowWidth = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const p = urlParams.get("parentWidth");

    if (p) {
      setParentWindowWidth(p);
    }
  };

  // Getting parent window height
  const getParentWindowHeight = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const p = urlParams.get("parentHeight");

    if (p) {
      setParentWindowHeight(p);
    }
  };

  const openChatApp = () => {
    createHayleyRecord("hayley");

    getConversationId(SSID)
      .then((res) => res.json())
      .then((res) => dispatch(setConversationID(res?.data?.createHayleyConversation?.hayleyConversation?.id || null)));

    dispatch(setIsOpen(true));
    const propertyId = getPropertyId();

    const requestOption = {
      tag: "SHOW_TO_CONVERSATION",
      metric: "Conversation",
      propertyId: propertyId,
      sessionId: SSID,
      appSubsection: "Start a conversation",
    };
    localStorage.removeItem(`events_${SSID}`);
    createEventSignal(requestOption);
  };

  const manageWindowSize = useCallback(() => {
    if (!isReady) {
      return;
    }

    const hotKeysHeight = getWidgetRefHeight(position, hotkeysRef?.current, mascotHeight);
    const widgetHeightNum = parseInt(widgetHeight, 10);
    const hotKeysHeightNum = parseInt(hotKeysHeight, 10);
    const heightNum = hotKeysHeightNum > widgetHeightNum ? hotKeysHeightNum : widgetHeightNum;
    const height = heightNum + "px";

    dispatch(setIsmobileWindow(isMobile));

    const hayleyArea = document.getElementById("hayley-area");
    const bodyElement = document.getElementsByTagName("body")[0];
    const hayleyDiv = document.querySelector("#hayley-area > div");

    setTimeout(() => {
      // remove original opacity & translate classes
      if (hotkeysRef?.current) {
        hotkeysRef.current.classList.remove("hayley-translate-y-10");
        hotkeysRef.current.classList.remove("hayley-opacity-0");
      }
    }, 500);

    if (hayleyArea && position === "bottom-left") {
      hayleyArea.style.alignItems = "flex-start";
    }

    if (isVideoTourScreen || isMobile || isPhotoTourScreen || isUnitTourScreen || isMaximized) {
      bodyElement.style.height = "100vh";
      bodyElement.style.width = "100%";
      hayleyArea.style.height = "100vh";
      hayleyArea.style.width = "100%";
      hayleyArea.style.padding = "0";

      if (isMobile && hayleyDiv) {
        hayleyDiv.style.height = isOpen ? "100vh" : height;
        hayleyDiv.style.width = "100%";
      }
    } else {
      bodyElement.style.height = "fit-content";
      bodyElement.style.width = "fit-content";
      hayleyArea.style.height = "fit-content";
      hayleyArea.style.width = "fit-content";
      hayleyArea.style.padding = "1px";
    }

    const setMinWidthHeight = (minWidth, minHeight) => {
      const minHeights = minHeight;

      hayleyArea.style.minWidth = minWidth;
      hayleyArea.style.minHeight = minHeights;
      bodyElement.style.minWidth = minWidth;
      bodyElement.style.minHeight = minHeights;
    };

    if (position !== "sticky-right") {
      if (!isMobile && !(isVideoTourScreen || isPhotoTourScreen || isUnitTourScreen || isMaximized)) {
        // hayleyArea.style.padding = "1px 17px 24px 2px";
      }

      setMinWidthHeight("fit-content", height);
    } else {
      setMinWidthHeight("62px", "120px");
    }

    const setAlignment = (alignment) => {
      hayleyArea.style.alignItems = alignment;
    };

    if (position === "bottom-left") {
      setAlignment("flex-start");
      // hayleyArea.style.padding = 0;
    } else if (position === "bottom-right") {
      setAlignment("flex-end");
      // hayleyArea.style.padding = 0;
    } else {
      setAlignment("center");
    }

    if (isParentView) {
      hayleyArea.style.height = "100vh";
      hayleyArea.style.width = "100vw";
      bodyElement.style.height = "100vh";

      if (hayleyDiv) {
        hayleyDiv.style.height = "100vh";
      }

      hayleyArea.style.alignItems = "center";

      if (isMaximized) {
        if (hayleyDiv) {
          hayleyDiv.style.width = "100%";
        }
        // hayleyDiv.style.padding = "30px";

        bodyElement.style.width = "100%";
      }
    }

    const hotKeysWidth = hotkeysRef?.current?.scrollWidth || 0;
    const isHotKeys = mascotSettings?.bubbleType === "none" || !mascotSettings?.bubbleType;

    window.parent.postMessage(
      {
        type: "hayley-resize",
        height: position === "sticky-right" && !isOpen ? `${mascotHeight}px` : !isOpen ? height : `${document.body.scrollHeight}px`,
        width:
          position === "sticky-right" || isOpen
            ? `${!isOpen ? 62 : document.body.scrollWidth}px`
            : `${hotKeysWidth > 20 ? hotKeysWidth + 10 : isHotKeys ? 90 : document.body.scrollWidth}px`,
        noSpace: isMaximized,
        isOpen,
        isVideoTourScreen,
        isMaximized,
        isPhotoTourScreen,
        isUnitTourScreen,
        isMobile,
        position,
      },
      "*"
    );
  }, [
    dispatch,
    isMaximized,
    isMobile,
    isOpen,
    isParentView,
    isPhotoTourScreen,
    isReady,
    isUnitTourScreen,
    isVideoTourScreen,
    mascotHeight,
    mascotSettings?.bubbleType,
    position,
    widgetHeight,
    hotkeysWidth,
  ]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (hotkeysRef.current) {
        setHotkeysWidth(hotkeysRef.current.scrollWidth || 0);
      }
    });

    if (hotkeysRef.current) {
      observer.observe(hotkeysRef.current);
    }

    return () => {
      if (hotkeysRef.current) {
        observer.unobserve(hotkeysRef.current);
      }
    };
  }, [hotkeysRef.current]);

  const createHayleyRecord = useCallback(
    (event = "impression") => {
      const propertyId = getPropertyId();
      const isJAI = getIsJAI();

      if (!isJAI) {
        createItemRecord(propertyId, SSID, event);
      }
    },
    [SSID]
  );

  const getDefaultHayleyMessage = useCallback(() => (isMultiLanguage ? "welcomeMessage" : "Hey, I'm Hayley. How can I help you?"), [isMultiLanguage]);

  const fetchSSID = () =>
    setTimeout(() => {
      if (ssidRef.current !== null) {
        return;
      }

      try {
        const fetchData = async () => {
          const urlParams = new URLSearchParams(window.location.search);
          const env = urlParams.get("env");
          const apiHost = { qa: "qa.hyly.us", stg: "my.hyly.us" }[env] || "my.hy.ly";
          const chatHost = { qa: "chat-qa.hyly.ai", stg: "chat-stg.hyly.ai" }[env] || "chat.hyly.ai";
          const ssidIframe = document.createElement("iframe");
          const src = `https://${apiHost}/chat/ssid?page_url=${encodeURIComponent(window.location.protocol + "//" + chatHost)}`;

          ssidIframe.setAttribute("src", src);
          ssidIframe.setAttribute("style", "width:1px; height:1px; visibility: hidden;");

          let ssidTimer = setInterval(function () {
            if (document.body) {
              document.body.appendChild(ssidIframe);
              clearInterval(ssidTimer);
            }
          }, 200);
        };

        fetchData();
      } catch (error) {
        console.error(error);
      }
    }, 1500);

  useEffect(() => {
    const receiveSsidListener = (event) => {
      const { data } = event;

      if (data) {
        const chatOpen = data.chatOpen;

        if (chatOpen === 1) {
          openChatApp();
        }
      }

      if (data.ssid) {
        dispatch(setAppSSID(data.ssid));
        ssidRef.current = data.ssid;

        return;
      }

      if (typeof data === "string") {
        let ssid;
        try {
          const parsedData = JSON.parse(data);
          ssid = parsedData.ssid;
        } catch (error) {
          ssid = null;
        }

        if (ssid) {
          dispatch(setAppSSID(ssid));
        }
      }
    };

    window.addEventListener("message", receiveSsidListener);

    return () => window.removeEventListener("message", receiveSsidListener);
  }, [dispatch]);

  // Handling standalone page = "available-units"
  // load units according to the URL params and move user to unit view table
  const handleLoadUnits = async () => {
    dispatch(setIsDisabled(true));
    const fp_id = getUrlParam("fp");
    const moveIns = getUrlParam("move");
    const budgets = getUrlParam("price");
    const bedrooms = getUrlParam("bedroom");
    const propertyId = getUrlParam("property_id");

    let filter = {
      moveIns: moveIns,
      budgets: budgets,
      bedrooms: bedrooms,
      allFloorPlans: { id: fp_id, name: fp_id },
    };

    try {
      const budgets = getPriceRange(filter.budgets) || null;

      const length = budgets?.length || 0;

      const desiredMoveIn = filter["moveIns"];

      const min = length === 0 ? null : length > 1 ? budgets[0] : 0;
      const max = length === 0 ? null : length > 1 ? budgets[1] : budgets[0];

      let convertedDate = desiredMoveIn;
      let isRange = false;

      if (moment(desiredMoveIn, "MMM DD, YYYY", true).isValid()) {
        convertedDate = moment(desiredMoveIn, "MMM DD, YYYY").format("YYYY-MM-DD");
      }

      const alreadyDotted = desiredMoveIn?.startsWith("..");

      if (isValidDateRange(desiredMoveIn)) {
        isRange = true;
        convertedDate = desiredMoveIn.split(",")?.join("..");
      }

      const queryParams = `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}${filter.bedrooms ? `&bedroom_type=${filter.bedrooms}` : ""}${
        !desiredMoveIn || desiredMoveIn === "any"
          ? ""
          : `&desired_move_in=${(filter?.isAsap || !isRange) && !alreadyDotted ? "..." + convertedDate || desiredMoveIn : convertedDate || desiredMoveIn}`
      }${min ? `&min_price=${min}` : ""}${max ? `&max_price=${max}` : ""}`;

      const units = await getUnits(queryParams);
      const floor_plans = units?.data?.floor_plans?.map((i) => ({ name: i.fp_name, id: i.id }));
      const isSelectedFloorPresent = floor_plans.find((floor) => floor.id === fp_id);
      const visible = units?.data.search_result === false;

      if (visible) {
        filter["visible"] = visible;
      }

      if (isSelectedFloorPresent) {
        filter["allFloorPlans"] = isSelectedFloorPresent;
      } else {
        filter["allFloorPlans"] = {
          id: null,
          name: "All",
        };
      }

      dispatch(setUnitFilters(filter));

      const bedTypes = await getBedroomTypes(`?api_key=${encodeURIComponent(apiKey)}&property_id=${propertyId}`);

      const priceRange = await getPriceBrackets(
        `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}${filter.bedrooms ? `&bedroom_type=${filter.bedrooms}` : ""}`
      );

      dispatch(setIsUnitTourScreen(true));
      dispatch(setUnits(units?.data || null));
      dispatch(setBedRooms(bedTypes?.data || null));
      dispatch(setBudgets(priceRange?.data || null));
      dispatch(setAllFloorPlans(floor_plans || null));
      dispatch(removeAndAddMessages([]));
    } catch (e) {
      dispatch(removeAndAddMessages([{ message: ERROR_MSG, speaker: "bot", subject: "", createdAt: new Date().toISOString() }]));
    } finally {
      dispatch(setReplies(INITIAL_REPLIES));

      dispatch(setIsDisabled(false));

      dispatch(
        removeAndAddMessages([
          {
            message: getDefaultHayleyMessage(),
            speaker: "bot",
            type: !isMultiLanguage ? "translated" : null,
            createdAt: new Date().toISOString(),
            isInitMessage: true,
          },
        ])
      );
    }
  };

  const handlePrivacyPolicyDisclaimer = async (org_id) => {
    const rawResponse = await getPrivacyPolicyDisclaimer(org_id);
    let disclaimerInfo = await rawResponse.json();
    disclaimerInfo = disclaimerInfo?.data?.privacyPolicyDisclaimer;
    const formattedData = getFormattedDisclaimer(disclaimerInfo?.disclaimer);

    dispatch(
      setPrivacyPolicyDisclaimer({ message: formattedData?.message, message_es: formattedData?.message_es, isEnabled: !!disclaimerInfo?.disclaimerEnabled })
    );
  };

  const handleChatDisclaimer = async (org_id, property_id) => {
    const rawResponse = await getHayleyChatDisclaimer(org_id, property_id);
    let disclaimerInfo = await rawResponse.json();
    disclaimerInfo = disclaimerInfo?.data?.hayleyChatDisclaimer;
    const formattedData = getFormattedDisclaimer(disclaimerInfo?.orgLevelDisclaimer);

    dispatch(
      setFormDisclaimer({ message: formattedData?.message, message_es: formattedData?.message_es, isEnabled: !!disclaimerInfo?.orgLevelDisclaimerEnabled })
    );
  };

  useEffect(() => {
    fetchSSID();

    dispatch(loadInitialMessages({ page: getUrlParam("page") }));
  }, []);

  useEffect(() => {
    const propertyId = getPropertyId();

    if (!propertyId) {
      return;
    }

    const fetch = async () => {
      const mascotSettingResponse = await fetchMascotSettings(propertyId);

      const {
        data: {
          mascot: { mascotSetting },
        },
      } = await mascotSettingResponse.json();

      dispatch(setMascotSettings(mascotSetting));
      dispatch(setArtieLogo(mascotSetting.iconUrl));
      setIsReady(true);

      const hasFirstMessage = mascotSetting.firstMessage?.trim().length > 0;
      const page = getUrlParam("page");
      const type = getUrlParam("feedback_type") || "";
      const email = getUrlParam("email") || "";
      const unitsOnly = page == "available-units";
      const contactOnly = page == "contact-me";
      const feedbackOnly = page == "feedback";

      // If standalone page is feedback then show the feedback modal with correct params
      dispatch(setStandalonePage(page));
      dispatch(setStandaloneParams({ type, email }));

      if (feedbackOnly) {
        dispatch(setIsFeedbackModalVisible(true));
      }

      // Don't show first message when page is contact-me or available-units

      if (hasFirstMessage && !unitsOnly && !contactOnly && !feedbackOnly && (!isOpenRef.current || isParentView)) {
        dispatch(
          removeAndAddMessages([
            {
              message: hasFirstMessage ? mascotSetting.firstMessage.trim() : getDefaultHayleyMessage(),
              speaker: "bot",
              type: hasFirstMessage || !isMultiLanguage ? "translated" : null,
              createdAt: new Date().toISOString(),
              isInitMessage: true,
            },
          ])
        );
      }
    };

    fetch().then(() => getGtmId(propertyId));
  }, [dispatch, getDefaultHayleyMessage, isMultiLanguage, isParentView]);

  useEffect(() => {
    if (orgId) {
      handlePrivacyPolicyDisclaimer(orgId);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId && propertyId) {
      handleChatDisclaimer(orgId, propertyId);
    }
  }, [orgId, propertyId]);

  useEffect(() => {
    getPropertyData();
    getShortUrl();
    getPosition();
    getParentWindowWidth();
    getParentWindowHeight();
  }, [getPropertyData, getShortUrl]);

  useEffect(() => {
    if (!apiKey || !isFetchManagement) {
      return;
    }

    const propertyId = getPropertyId();

    getMascot(propertyId, apiKey);

    const unitsOnly = getUrlParam("page") == "available-units";

    getPromotionInfo(propertyId, apiKey).then(({ data: offers }) => {
      dispatch(setPromotionOffer(offers));
    });

    // page is available-units then load units and move user to unit view table
    if (unitsOnly) {
      handleLoadUnits();
    }
  }, [apiKey, dispatch, getMascot]);

  useEffect(() => {
    const height = getWidgetHeight(mascotSettings, messages, position, promotionOffer, mascotHeight);
    setWidgetHeight(height);
  }, [mascotSettings, messages, promotionOffer, mascotHeight, position]);

  useEffect(() => {
    if (!SSID) {
      return;
    }

    createHayleyRecord("impression");
  }, [SSID, createHayleyRecord]);

  useEffect(() => {
    manageWindowSize();
  }, [
    isOpen,
    isMaximized,
    isVideoTourScreen,
    isPhotoTourScreen,
    isUnitTourScreen,
    position,
    isReady,
    parentWindowWidth,
    parentWindowHeight,
    widgetHeight,
    hotkeysRef?.current?.scrollHeight,
    manageWindowSize,
    hotkeysWidth,
  ]);

  if (!isReady || !isShow || !visibleMascot) {
    return null;
  }

  if (!isOpen && position !== "sticky-right") {
    return (
      <div
        className={`hayley-relative hayley-flex hayley-h-fit hayley-w-fit hayley-cursor-pointer hayley-flex-col ${
          position === "bottom-right" ? "hayley-items-end" : "hayley-items-start"
        } hayley-justify-end hayley-space-y-2`}
      >
        <HotKeys position={position} openChatApp={openChatApp} createHayleyRecord={createHayleyRecord} hotkeysRef={hotkeysRef} />
        {isEmpty(artieLogo) ? (
          <div data-testid="hayley-mascot" className="hayley-group hayley-z-10 hayley-h-[80px]" onClick={openChatApp}>
            {mascotSettings?.icon ? renderMascot(mascotSettings, position) : null}
          </div>
        ) : (
          <div className="hayley-z-10 hayley-overflow-hidden" data-testid="hayley-mascot" onClick={openChatApp}>
            <div className="custom-mascot hayley-h-[90px] hayley-w-[90px] hayley-overflow-hidden hayley-rounded-full hayley-bg-white hayley-drop-shadow-[1px_2px_5px_rgba(221,221,221,0.40)]">
              <div className="hayley-flex hayley-h-[90px] hayley-items-center hayley-justify-center hover:hayley-bg-[#26bbed10]">
                <img
                  src={artieLogo}
                  alt=""
                  className={`hayley-h-[70px] hayley-w-[70px] ${
                    (!artieLogo.includes("data:image/gif") || (logoType && logoType !== "GIF")) && hayleyAnimation(mascotSettings?.animation)
                  } hayley-rounded-full hayley-object-cover`}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!isOpen && position === "sticky-right") {
    return (
      <div
        data-testid="hayley-mascot"
        className="hayley-z-10 hayley-flex hayley-cursor-pointer hayley-flex-col hayley-items-center hayley-justify-center hayley-space-y-3 hayley-bg-white hayley-p-[6px] hayley-shadow "
        onClick={openChatApp}
      >
        {isEmpty(artieLogo) && mascotSettings?.icon && renderMascot(mascotSettings, position)}
        {isEmpty(artieLogo) && !mascotSettings?.icon && null}
        {!isEmpty(artieLogo) && (
          <div className="hayley-z-11 hayley-flex hayley-h-14 hayley-w-14 hayley-items-center hayley-justify-center hayley-overflow-hidden hayley-rounded-full hayley-bg-white hayley-drop-shadow-[1px_2px_5px_rgba(221,221,221,0.40)] hover:hayley-bg-[#26bbed10]">
            <img
              src={artieLogo}
              alt=""
              className={`hayley-h-[50px] hayley-w-[50px] ${
                (!artieLogo.includes("data:image/gif") || (logoType && logoType !== "GIF")) && hayleyAnimation(mascotSettings?.animation)
              } hayley-rounded-full hayley-object-cover`}
            />
          </div>
        )}
        <Icon iconType="Arrow" className={`hayley-h-4 hayley-w-4`} style={{ color: backgroundColor }} />
      </div>
    );
  }

  const chatBotMaxScreen = () => {
    if (!isMobile) {
      dispatch(setIsMaximized(true));
    }
  };

  if (isVideoTourScreen) {
    chatBotMaxScreen();

    return <VideoTourBox />;
  }

  if (isPhotoTourScreen) {
    chatBotMaxScreen();

    return <PhotosView isMobile={isMobile} />;
  }

  if (isUnitTourScreen) {
    chatBotMaxScreen();

    return <UnitsView />;
  }

  return (
    <ChatbotContent shouldShowContent={shouldShowContent} position={position} parentWindowWidth={parentWindowWidth} parentWindowHeight={parentWindowHeight} />
  );
};

export default App;
